/**
 * Per-person celebration signatures. When a milestone is logged, the
 * Celebration overlay picks confetti particles that match the person's
 * chosen signature — so each family member has a recognizable "look".
 *
 * Stored in localStorage per person_id (no migration needed). Default:
 * deterministic hash of person_id → one of the six signatures.
 */

export type SignatureKind = 'confetti' | 'hearts' | 'stars' | 'photo_bloom' | 'sparkles' | 'ribbons'

export interface Signature {
  slug: SignatureKind
  label: string
  description: string
  glyph: string   // small unicode char used in the picker
  particle: 'circle' | 'heart' | 'star' | 'ring' | 'diamond' | 'streak'
  particle_count: number
  particle_size_range: [number, number]
  motion: 'fall' | 'burst' | 'twinkle' | 'bloom' | 'drift'
}

export const SIGNATURES: Signature[] = [
  { slug: 'confetti',    label: 'Confetti',    description: 'Soft dots falling from the top',    glyph: '·', particle: 'circle',  particle_count: 32, particle_size_range: [4, 10], motion: 'fall' },
  { slug: 'hearts',      label: 'Hearts',      description: 'Warm hearts drifting up',           glyph: '♥', particle: 'heart',   particle_count: 22, particle_size_range: [10, 18], motion: 'drift' },
  { slug: 'stars',       label: 'Stars',       description: 'Stars twinkling around the card',   glyph: '★', particle: 'star',    particle_count: 26, particle_size_range: [8, 16],  motion: 'twinkle' },
  { slug: 'photo_bloom', label: 'Photo bloom', description: 'Soft radial glow behind the card',  glyph: '◉', particle: 'ring',    particle_count: 8,  particle_size_range: [40, 120], motion: 'bloom' },
  { slug: 'sparkles',    label: 'Sparkles',    description: 'Tiny diamond sparks bursting out',  glyph: '✦', particle: 'diamond', particle_count: 40, particle_size_range: [3, 7],   motion: 'burst' },
  { slug: 'ribbons',     label: 'Ribbons',     description: 'Streamers falling in slow motion',  glyph: '❋', particle: 'streak',  particle_count: 16, particle_size_range: [2, 4],   motion: 'fall' },
]

const LS_PREFIX = 'wren.celebration.'

export function signatureForPerson(personId: string): Signature {
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem(LS_PREFIX + personId)
    const found = SIGNATURES.find((s) => s.slug === saved)
    if (found) return found
  }
  // Deterministic default: hash → index
  let hash = 5381
  for (let i = 0; i < personId.length; i++) hash = ((hash << 5) + hash) + personId.charCodeAt(i)
  const idx = Math.abs(hash) % SIGNATURES.length
  return SIGNATURES[idx]
}

export function setSignatureForPerson(personId: string, slug: SignatureKind): void {
  try { localStorage.setItem(LS_PREFIX + personId, slug) } catch { /* quota */ }
}
